import {
  OrderPickupLocation,
  OrderPickupPerson,
} from 'typescript/types/api/solidshop';
import t from 'typescript/utilities/translate';

// see docs on JS Intl.DateTimeFormat() constructor
type FormatOptionsObject = Pick<Intl.DateTimeFormatOptions, 'hour' | 'minute'>;

// e.g. '1.32 mi'
export const distanceToStore = (distanceInMiles: number) =>
  t('checkout.delivery.pickup.distanceInMiles', {
    num: Math.round(distanceInMiles * 100) / 100,
  });

// e.g. 'Best Buy, Jersey City'
export const fullLocationName = (pickupLocation: OrderPickupLocation) =>
  t('checkout.delivery.condensed.retailerLocation', {
    partnerStore: pickupLocation.partner_store,
    locationName: pickupLocation.location_name,
  });

// e.g. '123 Fake St., New York, NY 10012'
export const pickupLocationAddress = (pickupLocation: OrderPickupLocation) =>
  t('checkout.delivery.condensed.address', {
    addressLine1: pickupLocation.address_line_1,
    city: pickupLocation.city,
    state: pickupLocation.state,
    postalCode: pickupLocation.postal_code,
  });

// e.g. 'John Smith, jsmith@example.com, (310) 555-5555'
export const pickupPersonInfo = (pickupPerson: OrderPickupPerson) =>
  t('checkout.delivery.condensed.contactInfo', {
    firstName: pickupPerson.first_name,
    lastName: pickupPerson.last_name,
    email: pickupPerson.email,
    phone: pickupPerson.phone,
  });

// e.g. 'Open until 10 pm' or 'Open until 8:30 pm'
export const openUntilStr = (openUntilTime?: string | null): string | null => {
  if (!openUntilTime) return null;

  const dateTime = new Date(openUntilTime);
  if (Number.isNaN(dateTime.getTime())) return null;

  const options: FormatOptionsObject = { hour: 'numeric' };

  if (dateTime.getMinutes() !== 0) {
    options.minute = '2-digit';
  }

  try {
    const closingTime = new Intl.DateTimeFormat(undefined, options)
      .format(dateTime)
      .toLocaleLowerCase();

    return t('checkout.delivery.pickup.openUntil', { closingTime });
  } catch {
    // A malformed close time should not blank the entire pickup options page.
    return null;
  }
};

// Detect a date-only value like '2024-10-27' so the fallback keeps the
// calendar day intended by the API instead of drifting to the shopper's
// local offset from UTC midnight.
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// e.g. 'Wednesday, May 21'
export const weekdayAndDate = (
  date: string,
  timeZone?: string | null,
): string => {
  const dateTime = new Date(date);
  if (Number.isNaN(dateTime.getTime())) return date;

  const isDateOnly = DATE_ONLY_PATTERN.test(date);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  };

  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(dateTime);
  } catch {
    try {
      return new Intl.DateTimeFormat(undefined, {
        ...options,
        ...(isDateOnly ? { timeZone: 'UTC' } : {}),
      }).format(dateTime);
    } catch {
      return date;
    }
  }
};

// e.g. 'Ready today', 'Ready tomorrow', or 'Ready Wednesday, May 21'
export const readyDateStr = (
  daysUntilAvailable: number | null,
  availabilityDate: string,
  timeZone?: string | null,
) => {
  let day;

  switch (daysUntilAvailable) {
    case 0: {
      day = t('checkout.delivery.pickup.today');
      break;
    }
    case 1: {
      day = t('checkout.delivery.pickup.tomorrow');
      break;
    }
    default: {
      day = weekdayAndDate(availabilityDate, timeZone);
    }
  }

  return t('checkout.delivery.pickup.readyDate', { date: day });
};

// e.g. 'Ready in 90 minutes' or 'Ready in 2 hours'
export const durationStr = (readyInMinutes: number) => {
  let duration: string;
  let hours: number;

  if (!readyInMinutes) return null;

  // e.g. 30 minutes, 90 minutes, but not 60 minutes
  if (readyInMinutes !== 60 && readyInMinutes <= 90) {
    duration = t('checkout.delivery.pickup.xMinutes', { x: readyInMinutes });
  } else {
    hours = readyInMinutes / 60;
    // if ready in 150 minutes, let's round up to 3 hours
    if (readyInMinutes % 60 > 0) hours += 1;
    duration = t('checkout.delivery.pickup.xHours', { x: hours });
  }

  return t('checkout.delivery.pickup.readyInDuration', { duration });
};
